import { Injectable } from '@angular/core';
import { NotificationService } from '@canvas/four-r-digital';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { FinanceApiService } from './finance-api.service';
import {
  FinanceOverviewResponse,
  FundingSummary,
  PartnerContribution,
  PaymentDraft,
  PaymentRecord,
  FinanceAsset,
} from './finance.models';

interface FinanceState {
  summary?: FundingSummary;
  assets: FinanceAsset[];
  partners: PartnerContribution[];
  payments: PaymentRecord[];
  loaded: boolean;
}

const emptySummary: FundingSummary = {
  totalEquity: 0,
  paid: 0,
  remaining: 0,
  healthPercent: 0,
  growthSeries: [],
  projectedRepayment: [],
  actualRepayment: [],
};

const emptyOverview: FinanceOverviewResponse = {
  summary: emptySummary,
  assets: [],
  partners: [],
  payments: [],
};

@Injectable({ providedIn: 'root' })
export class FinanceStateService {
  private readonly state = new BehaviorSubject<FinanceState>({
    summary: undefined,
    assets: [],
    partners: [],
    payments: [],
    loaded: false,
  });

  summary$ = this.state.pipe(
    map((state) => state.summary),
    map((summary) => summary ?? emptySummary)
  );

  assets$ = this.state.pipe(
    map((state) => state.assets),
    map((assets) => assets)
  );

  partners$ = this.state.pipe(
    map((state) => state.partners),
    map((partners) => partners)
  );

  payments$ = this.state.pipe(
    map((state) => state.payments),
    map((payments) => payments)
  );

  constructor(
    private readonly financeApiService: FinanceApiService,
    private readonly toaster: NotificationService
  ) {}

  loadData(forceReload = false): void {
    if (this.state.value.loaded && !forceReload) {
      return;
    }

    this.financeApiService
      .getOverview()
      .pipe(
        tap((response) => {
          const normalized = this.normalizeOverview(response);
          this.state.next({ ...normalized, loaded: true });
          this.financeApiService.saveLocalOverview(normalized);
        }),
        catchError(() => {
          this.state.next({ ...emptyOverview, loaded: true });
          return of(emptyOverview);
        })
      )
      .subscribe();
  }

  createPayment(draft: PaymentDraft): Observable<string> {
    return this.financeApiService
      .createPayment(draft)
      .pipe(
        map((response) => response.reference),
        tap((reference: string) => {
          this.applyPaymentDraft(draft, reference);
          this.toaster.showSuccess(
            `Reference: ${reference}`,
            'Payment submitted'
          );
        }),
        catchError(() => {
          const fallbackReference = `LOCAL-${Date.now()}`;
          this.applyPaymentDraft(draft, fallbackReference);
          this.toaster.showInfo(
            `Reference: ${fallbackReference}`,
            'Payment captured in local mode'
          );
          return of(fallbackReference);
        })
      );
  }

  private applyPaymentDraft(draft: PaymentDraft, reference: string): void {
    const current = this.state.value.loaded
      ? this.state.value
      : { ...emptyOverview, loaded: true };

    const selectedAsset = current.assets.find((asset) => asset.id === draft.targetId);

    const summary = current.summary ?? emptySummary;
    const paid = summary.paid + draft.amount;
    const remaining = Math.max(0, summary.totalEquity - paid);
    const healthPercent =
      summary.totalEquity > 0
        ? Math.min(100, Math.round((paid / summary.totalEquity) * 100))
        : 0;

    const contributionInThousands = Number((draft.amount / 1000).toFixed(2));
    const nextSummary: FundingSummary = {
      ...summary,
      paid,
      remaining,
      healthPercent,
      growthSeries: this.bumpSeries(summary.growthSeries, contributionInThousands),
      projectedRepayment: this.bumpSeries(
        summary.projectedRepayment,
        contributionInThousands * 0.5
      ),
      actualRepayment: this.bumpSeries(
        summary.actualRepayment,
        contributionInThousands
      ),
    };

    const nextAssets = current.assets.map((asset) => {
      if (asset.id !== draft.targetId) {
        return asset;
      }

      const nextPaid = asset.paid + draft.amount;
      const paidPercent = asset.total > 0 ? nextPaid / asset.total : 0;

      return {
        ...asset,
        paid: nextPaid,
        paymentStatus: this.getPaymentStatus(paidPercent),
        velocitySeries: this.bumpSeries(asset.velocitySeries, 1),
      };
    });

    const nextPartners = current.partners.map((partner) =>
      partner.id === 'p-1'
        ? { ...partner, contribution: partner.contribution + draft.amount }
        : partner
    );

    const payment: PaymentRecord = {
      id: reference,
      targetName:
        draft.targetType === 'group'
          ? 'Lagos Tech Hub IV'
          : selectedAsset?.name ?? 'Asset',
      amount: draft.amount,
      method: draft.method,
      createdOn: new Date().toISOString(),
      status: 'Completed',
    };

    const nextOverview: FinanceOverviewResponse = {
      summary: nextSummary,
      assets: nextAssets,
      partners: nextPartners,
      payments: [payment, ...current.payments].slice(0, 10),
    };

    this.state.next({
      ...current,
      ...nextOverview,
      loaded: true,
    });

    this.financeApiService.saveLocalOverview(nextOverview);
  }

  private bumpSeries(series: number[], delta: number): number[] {
    if (!Array.isArray(series) || series.length === 0) {
      return [Number(delta.toFixed(2))];
    }

    const updated = [...series];
    const lastIndex = updated.length - 1;
    updated[lastIndex] = Number((updated[lastIndex] + delta).toFixed(2));
    return updated;
  }

  private getPaymentStatus(paidPercent: number): FinanceAsset['paymentStatus'] {
    if (paidPercent >= 0.95) {
      return 'Near Final';
    }

    if (paidPercent >= 0.7) {
      return 'Accelerated';
    }

    if (paidPercent >= 0.5) {
      return 'On Track';
    }

    return 'Delayed';
  }

  private normalizeOverview(response: FinanceOverviewResponse): FinanceOverviewResponse {
    return {
      ...response,
      assets: (response.assets ?? []).map((asset) => this.normalizeAsset(asset)),
    };
  }

  private normalizeAsset(asset: FinanceAsset): FinanceAsset {
    const source = asset as unknown as Record<string, unknown>;
    const usageSeries = this.getNumericSeries(source, [
      'usageSeries',
      'dailyUsageSeries',
      'milesPerDaySeries',
      'kwhPerDaySeries',
      'energyUsageSeries',
      'utilizationSeries',
    ]);

    const latitude = this.getNumber(source, ['latitude', 'lat']);
    const longitude = this.getNumber(source, ['longitude', 'lng', 'lon']);

    const nestedLocation = this.getRecord(source, [
      'lastKnownPosition',
      'lastKnownLocation',
      'location',
      'position',
      'coordinates',
    ]);

    const nestedLat = nestedLocation
      ? this.getNumber(nestedLocation, ['latitude', 'lat'])
      : undefined;
    const nestedLng = nestedLocation
      ? this.getNumber(nestedLocation, ['longitude', 'lng', 'lon'])
      : undefined;

    const resolvedLat = latitude ?? nestedLat;
    const resolvedLng = longitude ?? nestedLng;

    const locationLabel = this.getString(source, [
      'locationName',
      'lastKnownLocationName',
      'locationLabel',
    ]) ??
      (nestedLocation
        ? this.getString(nestedLocation, ['label', 'name', 'locationName'])
        : undefined);

    const assetUserName =
      this.getString(source, [
        'assetUserName',
        'userName',
        'ownerName',
        'partnerName',
        'assignedUserName',
      ]) ?? asset.assetUserName;
    const lastOnlineAt =
      this.getString(source, [
        'lastOnlineAt',
        'lastSeenAt',
        'lastOnline',
        'updatedAt',
      ]) ?? asset.lastOnlineAt;

    return {
      ...asset,
      assetUserName,
      lastOnlineAt,
      usageSeries: usageSeries.length > 0 ? usageSeries : asset.usageSeries,
      lastKnownPosition:
        resolvedLat !== undefined && resolvedLng !== undefined
          ? {
              latitude: resolvedLat,
              longitude: resolvedLng,
              label: locationLabel ?? asset.lastKnownPosition?.label,
            }
          : asset.lastKnownPosition,
    };
  }

  private getRecord(
    source: Record<string, unknown>,
    keys: string[]
  ): Record<string, unknown> | undefined {
    for (const key of keys) {
      const value = source[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
      }
    }

    return undefined;
  }

  private getNumber(source: Record<string, unknown>, keys: string[]): number | undefined {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }

    return undefined;
  }

  private getString(source: Record<string, unknown>, keys: string[]): string | undefined {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return undefined;
  }

  private getNumericSeries(source: Record<string, unknown>, keys: string[]): number[] {
    for (const key of keys) {
      const value = source[key];
      if (Array.isArray(value)) {
        const numeric = value
          .map((item) => (typeof item === 'string' ? Number(item) : item))
          .filter((item): item is number => typeof item === 'number' && Number.isFinite(item));

        if (numeric.length > 0) {
          return numeric;
        }
      }
    }

    return [];
  }
}
